'use client';

import React, { useMemo } from 'react';
import { Grid, GridItem, useBreakpointValue } from '@skin-seoul/design-system';
import { createReviewStatsMap, getProductType, useProductService } from '@skin-seoul/service';
import { useReviewService } from '@skin-seoul/service';
import { getPriceProductBasicInfo, isProductInStock, sortProductsByScore } from '@skin-seoul/service';
import { decodeHTMLEntities } from '@skin-seoul/utils';
import ProductCard from '@/components/products/ProductCard';
import type { Currency } from '@skin-seoul/service';
interface TopRankedProductGridProps {
  selectedCategoryName?: string;
  currency: Currency;
}
const TopRankedProductGrid = ({
  selectedCategoryName,
  currency
}: TopRankedProductGridProps) => {
  const {
    useGetTopRankedProducts
  } = useProductService();
  const {
    useGetReviewStatistics
  } = useReviewService();
  const productLimit = useBreakpointValue({
    base: 5,
    tablet: 6,
    desktop: 9
  });
  const {
    data: productsData
  } = useGetTopRankedProducts({
    currency
  });
  const {
    data: reviewStats
  } = useGetReviewStatistics({
    productIds: productsData?.products.map(product => product.productId).join(',')
  });
  const reviewStatsMap = useMemo(() => createReviewStatsMap(reviewStats), [reviewStats]);
  const categoryFilteredProducts = useMemo(() => {
    if (!productsData?.products || !selectedCategoryName) {
      return [];
    }
    return productsData.products.filter(product => {
      if (!product.category || !Array.isArray(product.category)) {
        return false;
      }
      const categoryStrings = product.category.map(cat => decodeHTMLEntities(String(cat)));
      return categoryStrings.includes(selectedCategoryName);
    });
  }, [productsData?.products, selectedCategoryName]);
  const inStockProducts = useMemo(() => {
    return categoryFilteredProducts.filter(isProductInStock);
  }, [categoryFilteredProducts]);
  const sortedProducts = useMemo(() => {
    return sortProductsByScore(inStockProducts);
  }, [inStockProducts]);
  const processedProducts = useMemo(() => {
    return sortedProducts.slice(0, productLimit);
  }, [sortedProducts, productLimit]);
  return <Grid templateColumns={{
    base: 'repeat(1, minmax(0, 1fr))',
    tablet: 'repeat(2, minmax(0, 1fr))',
    desktop: 'repeat(3, minmax(0, 1fr))'
  }} base={{
    '& > *:nth-of-type(n+6)': {
      display: 'none'
    }
  }} tablet={{
    '& > *:nth-of-type(n+7)': {
      display: 'none'
    }
  }} desktop={{
    '& > *:nth-of-type(n+1)': {
      display: 'initial'
    }
  }} gap={{
    base: 3,
    tablet: 4
  }} width="100%" data-sentry-element="Grid" data-sentry-component="TopRankedProductGrid" data-sentry-source-file="TopRankedProductGrid.tsx">
      {processedProducts.map((product, index) => {
      const lowestPriceProductInfo = getPriceProductBasicInfo(product);
      const productType = getProductType(product);
      const productReviewStats = reviewStatsMap[product.productId];
      return <GridItem key={product.productId} minWidth={0} height="fit-content">
            <ProductCard href={product.productLink} productType={productType} orientation="horizontal" ranking={index + 1} name={product.name} brand={product.brand} productImage={product.productImageUrls[0]} sku={product.sku} currency={product.currency} originalPrice={lowestPriceProductInfo.originalPrice} salePrice={lowestPriceProductInfo.salePrice} rating={productReviewStats?.averageRate} reviewCount={productReviewStats?.totalReviews} productId={product.productId.toString()} category={product.category} />
          </GridItem>;
    })}
    </Grid>;
};
export default TopRankedProductGrid;